import { useCallback, useEffect, useState } from 'react'
import {
  shopLanguageGetService,
  shopTextsGetService,
  shopTextsUpdateService,
} from 'src/api/shop'

const KEY = 'portal_sign_in'
const DEFAULT_LABEL = 'Sign in'

// Shared by the menu row, the header preview and the landing page preview.
let cache = null // { languageCode, label, defaultText }
let request = null
const listeners = new Set()

const load = () => {
  if (!request) {
    request = shopLanguageGetService()
      .then(response => response?.data?.languageCode || 'en')
      .then(languageCode =>
        shopTextsGetService(languageCode).then(response => {
          const text = (response?.data?.groups || [])
            .flatMap(group => group.texts || [])
            .find(item => item.key === KEY)
          cache = {
            languageCode,
            label: text?.text || text?.defaultText || DEFAULT_LABEL,
            defaultText: text?.defaultText || DEFAULT_LABEL,
          }
          return cache
        }),
      )
      .catch(() => {
        request = null
        return cache
      })
  }
  return request
}

/**
 * The portal's "Sign in" label: the workspace's text in its language
 * (Workspace › Language › Portal), which the learner app's header shows.
 * Changing it keeps the /login address.
 */
const useSignInLabel = () => {
  const [state, setState] = useState(cache)

  useEffect(() => {
    let active = true
    load().then(next => active && next && setState(next))
    listeners.add(setState)
    return () => {
      active = false
      listeners.delete(setState)
    }
  }, [])

  const save = useCallback(async label => {
    const current = cache || (await load())
    const languageCode = current?.languageCode || 'en'
    const defaultText = current?.defaultText || DEFAULT_LABEL
    const value = label.trim()
    await shopTextsUpdateService(languageCode, {
      // The language's own text is stored as no edit.
      [KEY]: value && value !== defaultText ? value : null,
    })
    cache = { languageCode, label: value || defaultText, defaultText }
    listeners.forEach(listener => listener(cache))
  }, [])

  return [state?.label || DEFAULT_LABEL, save]
}

export default useSignInLabel
